import * as React from "react"
import {
  Skeleton,
  SkeletonText,
  SkeletonTable,
  SkeletonCard,
} from "@/components/ui/skeleton"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { cn } from "@/lib/utils"

/* -----------------------------------------------------------------------------
 * Page Header Skeleton
 * -------------------------------------------------------------------------- */

interface PageHeaderSkeletonProps {
  className?: string
  withMetrics?: boolean
  metricsCount?: number
}

export function PageHeaderSkeleton({
  className,
  withMetrics = true,
  metricsCount = 3,
}: PageHeaderSkeletonProps) {
  return (
    <Card className={cn("page-intro border-primary/10", className)}>
      <CardHeader className="space-y-4 pb-0">
        {/* Kicker */}
        <Skeleton className="h-7 w-32 rounded-full" />
        {/* Title */}
        <Skeleton className="h-12 w-3/4 max-w-2xl md:h-14" />
        {/* Description */}
        <div className="space-y-2">
          <Skeleton className="h-5 w-full max-w-xl" />
          <Skeleton className="h-5 w-4/5 max-w-lg" />
        </div>
      </CardHeader>
      {withMetrics && (
        <CardContent className="pt-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: metricsCount }).map((_, i) => (
              <div
                key={i}
                className="rounded-xl border border-border/40 bg-muted/30 p-4"
              >
                <Skeleton className="mb-2 h-3 w-20" />
                <Skeleton className="h-8 w-16" />
                <Skeleton className="mt-2 h-4 w-full" />
              </div>
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  )
}

/* -----------------------------------------------------------------------------
 * Dashboard Page Skeleton
 * -------------------------------------------------------------------------- */

interface DashboardSkeletonProps {
  className?: string
  cardCount?: number
}

export function DashboardSkeleton({
  className,
  cardCount = 6,
}: DashboardSkeletonProps) {
  return (
    <div className={cn("grid gap-5 md:gap-6", className)}>
      <PageHeaderSkeleton />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: cardCount }).map((_, i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    </div>
  )
}

/* -----------------------------------------------------------------------------
 * Table Page Skeleton
 * -------------------------------------------------------------------------- */

interface TablePageSkeletonProps {
  className?: string
  rows?: number
  columns?: number
  withFilters?: boolean
}

export function TablePageSkeleton({
  className,
  rows = 8,
  columns = 5,
  withFilters = true,
}: TablePageSkeletonProps) {
  return (
    <div className={cn("grid gap-5 md:gap-6", className)}>
      <PageHeaderSkeleton withMetrics={false} />
      {withFilters && (
        <Card>
          <CardContent className="flex flex-wrap items-center gap-3 py-4">
            <Skeleton className="h-10 w-48" />
            <Skeleton className="h-10 w-36" />
            <Skeleton className="h-10 w-36" />
            <Skeleton className="ml-auto h-10 w-24" />
          </CardContent>
        </Card>
      )}
      <Card>
        <CardContent className="p-0">
          <SkeletonTable rows={rows} columns={columns} />
        </CardContent>
      </Card>
    </div>
  )
}

/* -----------------------------------------------------------------------------
 * Form Page Skeleton
 * -------------------------------------------------------------------------- */

interface FormPageSkeletonProps {
  className?: string
  fieldCount?: number
}

export function FormPageSkeleton({
  className,
  fieldCount = 4,
}: FormPageSkeletonProps) {
  return (
    <div className={cn("grid gap-5 md:gap-6", className)}>
      <PageHeaderSkeleton withMetrics={false} />
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent className="space-y-6">
          {Array.from({ length: fieldCount }).map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-11 w-full" />
            </div>
          ))}
          <div className="flex justify-end gap-3 pt-4">
            <Skeleton className="h-10 w-24" />
            <Skeleton className="h-10 w-32" />
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

/* -----------------------------------------------------------------------------
 * Detail Page Skeleton
 * -------------------------------------------------------------------------- */

interface DetailPageSkeletonProps {
  className?: string
  sections?: number
}

export function DetailPageSkeleton({
  className,
  sections = 3,
}: DetailPageSkeletonProps) {
  return (
    <div className={cn("grid gap-5 md:gap-6", className)}>
      <PageHeaderSkeleton withMetrics={false} />
      {Array.from({ length: sections }).map((_, i) => (
        <Card key={i}>
          <CardHeader>
            <Skeleton className="h-5 w-32" />
          </CardHeader>
          <CardContent>
            <SkeletonText lines={4} />
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

/* -----------------------------------------------------------------------------
 * Grid Page Skeleton (for card grids)
 * -------------------------------------------------------------------------- */

interface GridPageSkeletonProps {
  className?: string
  cardCount?: number
  columns?: 2 | 3 | 4
}

export function GridPageSkeleton({
  className,
  cardCount = 9,
  columns = 3,
}: GridPageSkeletonProps) {
  const colClasses = {
    2: "sm:grid-cols-2",
    3: "sm:grid-cols-2 lg:grid-cols-3",
    4: "sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4",
  }

  return (
    <div className={cn("grid gap-5 md:gap-6", className)}>
      <PageHeaderSkeleton withMetrics={false} />
      <div className={cn("grid gap-4", colClasses[columns])}>
        {Array.from({ length: cardCount }).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Skeleton className="size-12 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
              <div className="mt-4 space-y-2">
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-4/5" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}

export {
  PageHeaderSkeleton as PageSkeleton,
}
